import React, { useState, useEffect } from 'react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Input } from '../components/ui/input';
import { 
  Lock, 
  LogOut, 
  Download, 
  Users, 
  Mail, 
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  AlertCircle
} from 'lucide-react';
import { 
  adminLogin, 
  getContacts, 
  getWaitlist, 
  exportContacts, 
  exportWaitlist,
  getStats 
} from '../services/api';

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem('admin_token'));
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);

  const [activeTab, setActiveTab] = useState('contacts');
  const [contacts, setContacts] = useState({ submissions: [], total: 0 });
  const [waitlist, setWaitlist] = useState({ entries: [], total: 0 });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [contactsPage, setContactsPage] = useState(0);
  const [waitlistPage, setWaitlistPage] = useState(0);
  const pageSize = 10;

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError(null);

    try {
      const response = await adminLogin(password);
      setToken(response.access_token);
      localStorage.setItem('admin_token', response.access_token);
      setPassword('');
    } catch (err) {
      setLoginError('Invalid password');
    } finally {
      setLoginLoading(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    setToken(null);
    localStorage.removeItem('admin_token');
  };

  // Fetch data when token changes or tab/page changes
  useEffect(() => {
    if (!token) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch stats
        const statsData = await getStats(token);
        setStats(statsData);

        // Fetch current tab data
        if (activeTab === 'contacts') {
          const contactsData = await getContacts(token, contactsPage * pageSize, pageSize);
          setContacts(contactsData);
        } else {
          const waitlistData = await getWaitlist(token, waitlistPage * pageSize, pageSize);
          setWaitlist(waitlistData);
        }
      } catch (err) {
        if (err.response?.status === 401) {
          handleLogout();
          setError('Session expired. Please login again.');
        } else {
          setError('Failed to fetch data');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, activeTab, contactsPage, waitlistPage]);

  // Handle CSV export
  const handleExport = async (type) => {
    try {
      const blob = type === 'contacts' 
        ? await exportContacts(token)
        : await exportWaitlist(token);
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to export data');
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Login form
  if (!token) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <main className="pt-20">
          <div className="max-w-md mx-auto px-6 py-24">
            <div className="p-8 bg-white/[0.02] border border-white/10">
              <div className="w-16 h-16 flex items-center justify-center bg-[#C9A962]/10 mx-auto mb-6">
                <Lock size={28} className="text-[#C9A962]" />
              </div>
              <h1 className="text-2xl font-semibold text-white text-center mb-2">
                Admin Access
              </h1>
              <p className="text-white/60 text-sm text-center mb-8">
                Enter password to continue
              </p>

              {loginError && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 flex items-center gap-3 mb-4">
                  <AlertCircle size={18} className="text-red-500" />
                  <p className="text-red-400 text-sm">{loginError}</p>
                </div>
              )}

              <form onSubmit={handleLogin}>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#C9A962] focus:ring-[#C9A962]/20 rounded-none h-12 mb-4"
                  required
                />
                <button
                  type="submit"
                  disabled={loginLoading}
                  className="w-full py-3 bg-[#C9A962] text-black font-medium transition-all duration-300 hover:bg-[#C9A962]/10 hover:text-[#C9A962] disabled:opacity-50"
                >
                  {loginLoading ? 'Logging in...' : 'Login'}
                </button>
              </form>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Admin dashboard
  return (
    <div className="min-h-screen bg-black">
      <Header />
      <main className="pt-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-12">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-2xl font-semibold text-white">Admin Dashboard</h1>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm"
            >
              <LogOut size={18} />
              Logout
            </button>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="p-4 bg-white/[0.02] border border-white/10">
                <div className="flex items-center gap-3 mb-2">
                  <Mail size={18} className="text-[#C9A962]" />
                  <span className="text-white/60 text-sm">Total Contacts</span>
                </div>
                <p className="text-2xl font-semibold text-white">{stats.contacts.total}</p>
              </div>
              <div className="p-4 bg-white/[0.02] border border-white/10">
                <div className="flex items-center gap-3 mb-2">
                  <TrendingUp size={18} className="text-[#C9A962]" />
                  <span className="text-white/60 text-sm">Contacts (7d)</span>
                </div>
                <p className="text-2xl font-semibold text-white">{stats.contacts.last_7_days}</p>
              </div>
              <div className="p-4 bg-white/[0.02] border border-white/10">
                <div className="flex items-center gap-3 mb-2">
                  <Users size={18} className="text-[#C9A962]" />
                  <span className="text-white/60 text-sm">Total Waitlist</span>
                </div>
                <p className="text-2xl font-semibold text-white">{stats.waitlist.total}</p>
              </div>
              <div className="p-4 bg-white/[0.02] border border-white/10">
                <div className="flex items-center gap-3 mb-2">
                  <TrendingUp size={18} className="text-[#C9A962]" />
                  <span className="text-white/60 text-sm">Waitlist (7d)</span>
                </div>
                <p className="text-2xl font-semibold text-white">{stats.waitlist.last_7_days}</p>
              </div>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="p-4 bg-red-500/10 border border-red-500/30 flex items-center gap-3 mb-6">
              <AlertCircle size={18} className="text-red-500" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-4 mb-6 border-b border-white/10">
            <button
              onClick={() => { setActiveTab('contacts'); setContactsPage(0); }}
              className={`pb-3 px-2 text-sm font-medium transition-colors border-b-2 -mb-[1px] ${
                activeTab === 'contacts'
                  ? 'text-[#C9A962] border-[#C9A962]'
                  : 'text-white/60 border-transparent hover:text-white'
              }`}
            >
              Contact Submissions
            </button>
            <button
              onClick={() => { setActiveTab('waitlist'); setWaitlistPage(0); }}
              className={`pb-3 px-2 text-sm font-medium transition-colors border-b-2 -mb-[1px] ${
                activeTab === 'waitlist'
                  ? 'text-[#C9A962] border-[#C9A962]'
                  : 'text-white/60 border-transparent hover:text-white'
              }`}
            >
              Waitlist
            </button>
          </div>

          {/* Content */}
          {loading ? (
            <div className="text-center py-12">
              <p className="text-white/60">Loading...</p>
            </div>
          ) : activeTab === 'contacts' ? (
            <>
              {/* Export button */}
              <div className="flex justify-end mb-4">
                <button
                  onClick={() => handleExport('contacts')}
                  className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white text-sm hover:bg-white hover:text-black transition-all"
                >
                  <Download size={16} />
                  Export CSV
                </button>
              </div>

              {/* Contacts table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left text-white/60 text-xs font-medium py-3 px-4">Date</th>
                      <th className="text-left text-white/60 text-xs font-medium py-3 px-4">Name</th>
                      <th className="text-left text-white/60 text-xs font-medium py-3 px-4">Email</th>
                      <th className="text-left text-white/60 text-xs font-medium py-3 px-4">Subject</th>
                      <th className="text-left text-white/60 text-xs font-medium py-3 px-4">Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.submissions.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center text-white/40 py-8">
                          No contact submissions yet
                        </td>
                      </tr>
                    ) : (
                      contacts.submissions.map((contact) => (
                        <tr key={contact.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                          <td className="py-3 px-4 text-white/60 text-sm whitespace-nowrap">
                            {formatDate(contact.created_at)}
                          </td>
                          <td className="py-3 px-4 text-white text-sm">{contact.name}</td>
                          <td className="py-3 px-4 text-white text-sm">{contact.email}</td>
                          <td className="py-3 px-4 text-white text-sm">{contact.subject}</td>
                          <td className="py-3 px-4 text-white/80 text-sm max-w-xs truncate">
                            {contact.message}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {contacts.total > pageSize && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                  <p className="text-white/40 text-sm">
                    Showing {contactsPage * pageSize + 1}-{Math.min((contactsPage + 1) * pageSize, contacts.total)} of {contacts.total}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setContactsPage(p => Math.max(0, p - 1))}
                      disabled={contactsPage === 0}
                      className="p-2 bg-white/10 text-white disabled:opacity-30 hover:bg-white/20 transition-colors"
                    >
                      <ChevronLeft size={18} />
                    </button>
                    <button
                      onClick={() => setContactsPage(p => p + 1)}
                      disabled={(contactsPage + 1) * pageSize >= contacts.total}
                      className="p-2 bg-white/10 text-white disabled:opacity-30 hover:bg-white/20 transition-colors"
                    >
                      <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Export button */}
              <div className="flex justify-end mb-4">
                <button
                  onClick={() => handleExport('waitlist')}
                  className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white text-sm hover:bg-white hover:text-black transition-all"
                >
                  <Download size={16} />
                  Export CSV
                </button>
              </div>

              {/* Waitlist table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left text-white/60 text-xs font-medium py-3 px-4">Date</th>
                      <th className="text-left text-white/60 text-xs font-medium py-3 px-4">Name</th>
                      <th className="text-left text-white/60 text-xs font-medium py-3 px-4">Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {waitlist.entries.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="text-center text-white/40 py-8">
                          No waitlist entries yet
                        </td>
                      </tr>
                    ) : (
                      waitlist.entries.map((entry) => (
                        <tr key={entry.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                          <td className="py-3 px-4 text-white/60 text-sm whitespace-nowrap">
                            {formatDate(entry.created_at)}
                          </td>
                          <td className="py-3 px-4 text-white text-sm">{entry.name || '—'}</td>
                          <td className="py-3 px-4 text-white text-sm">{entry.email}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {waitlist.total > pageSize && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                  <p className="text-white/40 text-sm">
                    Showing {waitlistPage * pageSize + 1}-{Math.min((waitlistPage + 1) * pageSize, waitlist.total)} of {waitlist.total}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setWaitlistPage(p => Math.max(0, p - 1))}
                      disabled={waitlistPage === 0}
                      className="p-2 bg-white/10 text-white disabled:opacity-30 hover:bg-white/20 transition-colors"
                    >
                      <ChevronLeft size={18} />
                    </button>
                    <button
                      onClick={() => setWaitlistPage(p => p + 1)}
                      disabled={(waitlistPage + 1) * pageSize >= waitlist.total}
                      className="p-2 bg-white/10 text-white disabled:opacity-30 hover:bg-white/20 transition-colors"
                    >
                      <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
